import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { PageHeader, Input, Labeled } from "./Rentals";
import { Building2, Briefcase, Clock, BadgeCheck } from "lucide-react";
import { DatePicker } from "../lib/date-pickers";
import { PhoneField, PreferredContactMethodField } from "../components/PhoneField";
import { buildPhoneFieldValue, getPhoneFieldError, touchPhoneField } from "../lib/phone";

export default function LongTerm() {
  const today = new Date();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: buildPhoneFieldValue(),
    preferred_contact_method: null,
    start_date: today,
    duration: "1 month",
    client_type: "private",
    estimated_usage: "",
    passengers: 9,
    message: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const phoneError = getPhoneFieldError(form.phone);
    if (phoneError) {
      setForm({ ...form, phone: touchPhoneField(form.phone) });
      toast.error(phoneError);
      return;
    }
    setBusy(true);
    try {
      await api.post("/inquiries?kind=long_term", {
        ...form,
        phone: form.phone.e164 || null,
        phone_country: form.phone.country || null,
        start_date: form.start_date ? new Date(form.start_date).toISOString().slice(0, 10) : null,
        passengers: Number(form.passengers) || null,
      });
      toast.success("Thanks — we'll prepare a long-term offer and reach out.");
      setForm({ ...form, message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Long-term hire"
        title="Monthly van hire for companies and private clients."
        subtitle="Better rates, flexible agreements and dedicated support for 1+ month rentals. Tell us what you need and we'll come back with a tailored offer."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-10 grid gap-6 md:grid-cols-4" data-testid="long-term-benefits">
        <Benefit icon={<Clock size={18} />} title="Flexible terms" desc="From 1 month up to 12+ months." />
        <Benefit icon={<Briefcase size={18} />} title="Agencies welcome" desc="Partner and reseller rates available." />
        <Benefit icon={<Building2 size={18} />} title="B2B invoicing" desc="VAT, PO and monthly billing." />
        <Benefit icon={<BadgeCheck size={18} />} title="Dedicated contact" desc="One point of contact, fast response." />
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 grid gap-10 lg:grid-cols-5">
        <form onSubmit={submit} data-testid="long-term-form" className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Request a long-term offer</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Labeled label="Full name"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lt-name" /></Labeled>
            <Labeled label="Company (optional)"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="lt-company" /></Labeled>
            <Labeled label="Email"><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lt-email" /></Labeled>
            <Labeled label="Phone / WhatsApp">
              <PhoneField
                value={form.phone}
                onChange={(phone) => setForm({ ...form, phone })}
                countryTestId="lt-phone-country"
                numberTestId="lt-phone"
                errorTestId="lt-phone-error"
              />
            </Labeled>
            <Labeled label="Preferred contact method">
              <PreferredContactMethodField
                value={form.preferred_contact_method}
                onChange={(preferred_contact_method) => setForm({ ...form, preferred_contact_method })}
                testid="lt-contact-method"
              />
            </Labeled>
            <Labeled label="Start date">
              <DatePicker value={form.start_date} onChange={(d) => setForm({ ...form, start_date: d })} min={today} data-testid="lt-start" />
            </Labeled>
            <Labeled label="Expected duration">
              <select value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="lt-duration" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 outline-none">
                <option>1 month</option>
                <option>2–3 months</option>
                <option>3–6 months</option>
                <option>6+ months</option>
              </select>
            </Labeled>
            <Labeled label="Client type">
              <select value={form.client_type} onChange={(e) => setForm({ ...form, client_type: e.target.value })} data-testid="lt-client-type" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 outline-none">
                <option value="private">Private</option>
                <option value="company">Company</option>
                <option value="agency">Agency</option>
              </select>
            </Labeled>
            <Labeled label="Passengers needed (incl. driver)"><Input type="number" min="1" max="9" value={form.passengers} onChange={(e) => setForm({ ...form, passengers: e.target.value })} data-testid="lt-pax" /></Labeled>
          </div>
          <Labeled label="Estimated usage (km / days per week)">
            <Input placeholder="e.g. 1000 km / month, 6 days / week" value={form.estimated_usage} onChange={(e) => setForm({ ...form, estimated_usage: e.target.value })} data-testid="lt-usage" />
          </Labeled>
          <Labeled label="Anything else?">
            <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="lt-message" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 outline-none" />
          </Labeled>
          <button type="submit" disabled={busy} data-testid="lt-submit" className="rounded-md bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            {busy ? "Sending…" : "Request long-term offer"}
          </button>
        </form>

        <aside className="lg:col-span-2 space-y-6">
          <div className="rounded-xl bg-slate-900 text-white p-6">
            <h3 className="font-heading text-lg font-semibold">What happens next?</h3>
            <ol className="mt-3 space-y-2 text-sm text-slate-300 list-decimal list-inside">
              <li>We review your request (usually within one business day).</li>
              <li>We send a tailored offer by email or WhatsApp.</li>
              <li>We agree on terms and sign a simple long-term agreement.</li>
            </ol>
          </div>
          <div className="rounded-xl border border-slate-200 p-6 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">Typical use cases</p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-slate-600">
              <li>Production crews and touring teams</li>
              <li>Agencies serving seasonal clients</li>
              <li>Extended family stays</li>
              <li>Corporate shuttle contracts</li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}

function Benefit({ icon, title, desc }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="text-sky-700">{icon}</div>
      <p className="mt-3 font-semibold text-slate-900">{title}</p>
      <p className="mt-1 text-sm text-slate-600">{desc}</p>
    </div>
  );
}
